import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { WordHelpCard } from '../components/learning/WordHelpCard';
import type { ContentVocabularyItemDto } from '../services/contentService';
import { vocabularyService, type VocabularyMatchHit } from '../services/vocabularyService';
import type { StickyAudioMap } from '../utils/stickyAudio';
import { STICKY_AUDIO_UNDERLINE_ANIM_MS } from '../utils/stickyAudioUnderlineAnimation';
import { prefersReducedMotion } from '../utils/subscriptionAccessibility';
import {
  collectVocabularyItems,
  collectWordHelpText,
  mergeVocabularyItems,
  mergeWordHelpMaps,
  vocabularyToStickyMap,
  WORD_HELP_OPEN_EVENT,
  type WordHelpOpenDetail
} from '../utils/wordHelp';

export type WordHelpScope = 'local' | 'all';

type BlockLike = {
  type?: number;
  textContent?: string | null;
  textContentFa?: string | null;
  textFormatting?: string | null;
  textFormattingFa?: string | null;
  vocabularyItems?: ContentVocabularyItemDto[];
};

interface WordHelpBlockExit {
  id: number;
  phrases: ReadonlySet<string>;
}

interface WordHelpContextValue {
  scopeKind: 'lesson' | 'book';
  allowAllWords: boolean;
  /** This-lesson / all-words choice for one text block. */
  scopeFor: (blockId: number) => WordHelpScope;
  setScope: (blockId: number, scope: WordHelpScope) => void;
  loadingFor: (blockId: number) => boolean;
  /** Phrase map for one text block, including a short exit while underlines leave. */
  mapFor: (blockId: number) => StickyAudioMap;
  exitFor: (blockId: number) => WordHelpBlockExit;
  findEntry: (vocabId: number) => StickyAudioMap[string] | undefined;
  lessonId?: number;
  libraryBookId?: number;
  openDetail: WordHelpOpenDetail | null;
  closeCard: () => void;
  readOnly: boolean;
}

type BlockWordHelp = {
  scope: WordHelpScope;
  rendered: WordHelpScope;
  underlineExitId: number;
  exitingPhrases: ReadonlySet<string>;
};

const WordHelpContext = createContext<WordHelpContextValue | null>(null);
const EMPTY_PHRASES: ReadonlySet<string> = new Set();
const EMPTY_EXIT: WordHelpBlockExit = { id: 0, phrases: EMPTY_PHRASES };
const DEFAULT_BLOCK: BlockWordHelp = {
  scope: 'local',
  rendered: 'local',
  underlineExitId: 0,
  exitingPhrases: EMPTY_PHRASES
};

function phrasesLeavingScope(current: StickyAudioMap, next: StickyAudioMap): Set<string> {
  const staying = new Set(Object.keys(next));
  const leaving = new Set<string>();
  for (const key of Object.keys(current)) {
    if (!staying.has(key)) leaving.add(key);
  }
  return leaving;
}

function hitsToItems(hits: VocabularyMatchHit[]): ContentVocabularyItemDto[] {
  return hits.map((hit) => ({
    id: hit.id,
    entryKey: hit.entryKey,
    persian: hit.persian,
    english: hit.english ?? '',
    phonetic: hit.phonetic,
    partOfSpeech: hit.partOfSpeech,
    example: hit.example,
    imageUrl: hit.imageUrl,
    audioUrl: hit.audioUrl,
    audioFileName: hit.audioFileName,
    displayOrder: 0
  }));
}

export const WordHelpProvider: React.FC<{
  blocks: BlockLike[];
  scopeKind: 'lesson' | 'book';
  lessonId?: number;
  libraryBookId?: number;
  /** Load vocabulary saved on the lesson or book when this view does not include those blocks. */
  loadRemote?: boolean;
  allowAllWords?: boolean;
  /** Hide Leitner add/remove (admin lesson/book preview). */
  readOnly?: boolean;
  children: React.ReactNode;
}> = ({
  blocks,
  scopeKind,
  lessonId,
  libraryBookId,
  loadRemote = false,
  allowAllWords = true,
  readOnly = false,
  children
}) => {
  const [blockHelp, setBlockHelp] = useState<Record<number, BlockWordHelp>>({});
  const blockHelpRef = useRef(blockHelp);
  blockHelpRef.current = blockHelp;
  const [remoteItems, setRemoteItems] = useState<ContentVocabularyItemDto[]>([]);
  const [allState, setAllState] = useState<{ key: string; items: ContentVocabularyItemDto[] } | null>(null);
  const [loading, setLoading] = useState(false);
  const allCacheKeyRef = useRef<string | null>(null);
  const [openDetail, setOpenDetail] = useState<WordHelpOpenDetail | null>(null);
  const exitTimersRef = useRef<Record<number, number>>({});

  const localItems = useMemo(() => collectVocabularyItems(blocks), [blocks]);
  const text = useMemo(() => collectWordHelpText(blocks), [blocks]);

  useEffect(() => {
    if (!loadRemote) {
      setRemoteItems([]);
      return;
    }
    let cancelled = false;
    const run = async () => {
      try {
        const rows = lessonId
          ? await vocabularyService.forLesson(lessonId)
          : libraryBookId
            ? await vocabularyService.forBook(libraryBookId)
            : [];
        if (!cancelled) setRemoteItems(rows);
      } catch {
        if (!cancelled) setRemoteItems([]);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [loadRemote, lessonId, libraryBookId]);

  const localMap = useMemo(
    () => vocabularyToStickyMap(mergeVocabularyItems(localItems, remoteItems)),
    [localItems, remoteItems]
  );
  const allRequestKey = `${lessonId ?? ''}|${libraryBookId ?? ''}|${text}`;
  const allMap = useMemo(
    () => (allState?.key === allRequestKey ? vocabularyToStickyMap(allState.items) : null),
    [allState, allRequestKey]
  );
  const renderedAllMap = useMemo(
    () => (allMap ? mergeWordHelpMaps(localMap, allMap) : null),
    [allMap, localMap]
  );

  const localMapRef = useRef(localMap);
  const allMapRef = useRef(allMap);
  localMapRef.current = localMap;
  allMapRef.current = allMap;

  const writeBlock = useCallback((blockId: number, row: BlockWordHelp) => {
    const next = { ...blockHelpRef.current, [blockId]: row };
    blockHelpRef.current = next;
    setBlockHelp(next);
  }, []);

  const clearBlockTimer = useCallback((blockId: number) => {
    const timer = exitTimersRef.current[blockId];
    if (timer == null) return;
    window.clearTimeout(timer);
    delete exitTimersRef.current[blockId];
  }, []);

  useEffect(() => () => {
    Object.values(exitTimersRef.current).forEach((timer) => window.clearTimeout(timer));
  }, []);

  const setScope = useCallback((blockId: number, next: WordHelpScope) => {
    const resolved = allowAllWords ? next : 'local';
    const current = blockHelpRef.current[blockId] ?? DEFAULT_BLOCK;
    if (current.scope === resolved && current.rendered === resolved && current.exitingPhrases.size === 0) return;
    const previous = current.scope;
    setOpenDetail(null);
    if (resolved === 'all' && allCacheKeyRef.current !== allRequestKey) setLoading(true);

    const leavingAll = previous === 'all' && resolved === 'local';
    const currentAll = allMapRef.current;
    const leaving = leavingAll && currentAll
      ? phrasesLeavingScope(currentAll, localMapRef.current)
      : EMPTY_PHRASES;
    if (leavingAll && leaving.size > 0 && !prefersReducedMotion()) {
      clearBlockTimer(blockId);
      writeBlock(blockId, {
        scope: 'local',
        rendered: 'all',
        underlineExitId: current.underlineExitId + 1,
        exitingPhrases: leaving
      });
      exitTimersRef.current[blockId] = window.setTimeout(() => {
        delete exitTimersRef.current[blockId];
        const row = blockHelpRef.current[blockId] ?? DEFAULT_BLOCK;
        writeBlock(blockId, {
          ...row,
          scope: 'local',
          rendered: 'local',
          exitingPhrases: EMPTY_PHRASES
        });
      }, STICKY_AUDIO_UNDERLINE_ANIM_MS + 40);
      return;
    }

    const cancelExit = current.exitingPhrases.size > 0 || current.rendered !== current.scope;
    clearBlockTimer(blockId);
    writeBlock(blockId, {
      scope: resolved,
      rendered: resolved,
      underlineExitId: cancelExit ? current.underlineExitId + 1 : current.underlineExitId,
      exitingPhrases: EMPTY_PHRASES
    });
  }, [allowAllWords, allRequestKey, clearBlockTimer, writeBlock]);

  const anyWantsAll = Object.values(blockHelp).some((row) => row.scope === 'all');

  useEffect(() => {
    if (!anyWantsAll || !allowAllWords) return;
    if (allCacheKeyRef.current === allRequestKey) return;
    let cancelled = false;
    setLoading(true);
    void vocabularyService
      .match({
        text,
        ...(lessonId != null ? { lessonId } : {}),
        ...(libraryBookId != null ? { libraryBookId } : {})
      })
      .then((hits) => {
        if (cancelled) return;
        allCacheKeyRef.current = allRequestKey;
        setAllState({ key: allRequestKey, items: hitsToItems(hits) });
      })
      .catch(() => {
        if (cancelled) return;
        allCacheKeyRef.current = allRequestKey;
        setAllState({ key: allRequestKey, items: [] });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [anyWantsAll, allowAllWords, text, lessonId, libraryBookId, allRequestKey]);

  useEffect(() => {
    const onOpen = (event: Event) => {
      const detail = (event as CustomEvent<WordHelpOpenDetail>).detail;
      if (!detail) return;
      setOpenDetail(detail);
    };
    window.addEventListener(WORD_HELP_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(WORD_HELP_OPEN_EVENT, onOpen);
  }, []);

  const scopeFor = useCallback(
    (blockId: number) => blockHelp[blockId]?.scope ?? 'local',
    [blockHelp]
  );
  const loadingFor = useCallback(
    (blockId: number) => (blockHelp[blockId]?.scope === 'all') && allMap == null && loading,
    [blockHelp, allMap, loading]
  );
  const mapFor = useCallback((blockId: number) => {
    const rendered = blockHelp[blockId]?.rendered ?? 'local';
    return rendered === 'all' && renderedAllMap ? renderedAllMap : localMap;
  }, [blockHelp, renderedAllMap, localMap]);
  const exitFor = useCallback((blockId: number): WordHelpBlockExit => {
    const row = blockHelp[blockId];
    if (!row) return EMPTY_EXIT;
    return { id: row.underlineExitId, phrases: row.exitingPhrases };
  }, [blockHelp]);
  const findEntry = useCallback((vocabId: number) => {
    const maps = [allMapRef.current, localMapRef.current];
    for (const map of maps) {
      if (!map) continue;
      const entry = Object.values(map).find((row) => row.vocabId === vocabId);
      if (entry) return entry;
    }
    return undefined;
  }, []);

  const value = useMemo<WordHelpContextValue>(
    () => ({
      scopeKind,
      allowAllWords,
      scopeFor,
      setScope,
      loadingFor,
      mapFor,
      exitFor,
      findEntry,
      ...(lessonId != null ? { lessonId } : {}),
      ...(libraryBookId != null ? { libraryBookId } : {}),
      openDetail,
      closeCard: () => setOpenDetail(null),
      readOnly
    }),
    [scopeKind, allowAllWords, scopeFor, setScope, loadingFor, mapFor, exitFor, findEntry, lessonId, libraryBookId, openDetail, readOnly]
  );

  return (
    <WordHelpContext.Provider value={value}>
      {children}
      <WordHelpCardHost />
    </WordHelpContext.Provider>
  );
};

export function useWordHelpOptional(): WordHelpContextValue | null {
  return useContext(WordHelpContext);
}

export function useWordHelp(): WordHelpContextValue {
  const ctx = useContext(WordHelpContext);
  if (!ctx) throw new Error('Word help is not available on this screen.');
  return ctx;
}

const WordHelpCardHost: React.FC = () => {
  const ctx = useWordHelp();
  if (!ctx.openDetail) return null;
  const entry = ctx.findEntry(ctx.openDetail.vocabId);
  if (!entry) return null;
  return (
    <WordHelpCard
      entry={entry}
      rect={ctx.openDetail.rect}
      anchor={ctx.openDetail.anchor}
      lessonId={ctx.lessonId}
      libraryBookId={ctx.libraryBookId}
      readOnly={ctx.readOnly}
      onClose={ctx.closeCard}
    />
  );
};
